import type {
  ACLAction,
  ACLDirection,
  ACLL3RuleState,
  ACLResourceStatus,
  ACLResourceType,
} from '@/consts/acl'
import { NetGroup } from '@/models/instance'

export type Volume = {
  usage_bytes?: number
  usage_mb: number
  quota_gb: number
  init_quota_gb: number
  last_quota_gb: number
  type: number
  domain: number
  id: number
}

export type ServerUserInfo = {
  user: {
    id: number
    password: string
    createTime: {
      seconds: number
    }
    lastLoginAt: {
      seconds: number
    }
    mobile: string
    email: string
    blacklist: any
    name: string
    domain: number
    parentID: number
    scope: number
    scopeID: number
    scopeRole: number
    nickname: string
    alias: string
    note: string
    status: number
    passwordUpdateAt: {
      seconds: number
    }
  }
  globalSnapVolume: Volume
  partVolume: Volume
  invitationInfo: {
    invitationCode: string
    totalInvited: number
  }
  binds: any
  groups: {
    course: boolean
  }
  services: {
    '9': {
      accessableServices: Array<{
        label: string
        value: string
        weight: number
        authToken: string
      }>
    }
    '12': {
      accessableServices: Array<{
        label: string
        value: string
        weight: number
        authToken: string
      }>
    }
  }
  domains: Array<{
    id: number
    domain: number
  }>
  quotas: Array<{
    type: number
    value: number
  }>
  domainServicesVolume: Array<{
    domain: number
    services: {
      '9': {
        accessableServices: Array<{
          label: string
          value: string
          weight: number
          authToken: string
        }>
      }
      '12': {
        accessableServices: Array<{
          label: string
          value: string
          weight: number
          authToken: string
        }>
      }
    }
    volume: {
      usage_mb: number
      quota_gb: number
      init_quota_gb: number
    }
  }>
  registerableDomains: Array<number>
  suggestion: {
    needInitPassword: boolean
    needUpdatePassword: boolean
  }
  scopeInfo: {
    scope: number
    scopeID: number
    resID: string
    name: string
  }
}

export type ServerDomainInfo = {
  domain: number
  displayName: string
}

export type ServerMachineInfo = {
  maxAvailUnits: number
  unitSpec: {
    cpuCores: number
    cpuName: string
    gpuCores: number
    gpuName: string
    gpuRamMB: number
    ramMB: number
    diskGB: number
    attributes: {
      isSelfOwner: boolean
      isThirdParty: boolean
      isSpecialPrice: boolean
      isBetaTest: boolean
      isXEnabled: boolean
      isSnapEnabled: boolean
      isComposable: boolean
      isVGPU: boolean
      isMIG: boolean
      isDistributed: boolean
      isHyperContainer: boolean
      isWinEnabled: boolean
      isSupportNodeOp: boolean
      isECS: boolean
      isTimeSliced: boolean
    }
    priceMillicent: number
    discountPriceMillicent: number
    classTag: string
  }
  domains: number[]
  items: ServerItemInfo[]
  attachments?: ServerMachineAttachment[]
}

export type ServerMachineAttachment = {
  hardwareIDs?: number[]
  priceMilliCentPerUnit?: number
  priceMillicentPerUnit?: number
  category: number
  machineClassTag?: string
  Attachment?: {
    Cpu?: {
      coresPerUnit: number
      indexes?: number[]
    }
    Ram?: {
      MBPerUnit: number
    }
    Disk?: {
      diskGB: number
    }
  }
  items: ServerItemInfo[]
}

export type ServerPriceObject = {
  price: number
  unit: string
  text: string
}

export type ServerInstanceOrder = {
  autoRenewal: boolean
  createTime?: { seconds: number }
  deadlineTime?: { seconds: number }
  item?: {
    id: number
    catalog: number
    paymentType: number
    prices: { type: number; priceCent: number }[]
    desc: any
    sourceID: number
    qty: number
    deleted: boolean
    domain: number
    resID: string
  }
  id: number
  extra?: {
    orderToBilling: boolean
    purgeTime: any
  }
  renewPrice?: {
    type: number
    priceCent: number
  }
  domain: number
}

export type ServerInstanceInfo = {
  resID: string
  node: {
    id: number
    type: number
    hardware: {
      priceMillicent: number
      machine: {
        cpuIds: Array<number>
        cpuName: string
        ramMB: number
        diskGB: number
        diskName: string
        diskBandwidthMBs: number
        motherboardName: string
        pcieVer: string
        internetUploadMbs: number
        internetDownloadMbs: number
        totalCPUCores: number
        totalRAMMB: number
        bandwidthBit: number
        totalBandwidthBit: number
        arch: number
        pciConfig: any
        unitConfig: any
        gpuResourceLimit: any
      }
      gpu: {
        gpuIds: Array<number>
        gpuName: string
        gpuRamMB: number
        gpuMemBandwidthGBs: number
        gpuPCIELanes: number
        gpuPCIEBandwidthGBs: number
        maxCUDAVer: string
        tflops: number
        driver: string
        gpuModel: string
        uuids: any
        vendor: number
      }
      deadlineTime: {
        seconds: number
      }
      discountPriceMillicent: number
      timeLimitSec: number
      attributes: {
        isSelfOwner: boolean
        isThirdParty: boolean
        isSpecialPrice: boolean
        isBetaTest: boolean
        isXEnabled: boolean
        isSnapEnabled: boolean
        isComposable: boolean
        isVGPU: boolean
        isMIG: boolean
        isDistributed: boolean
        isHyperContainer: boolean
        isWinEnabled: boolean
        isSupportNodeOp: boolean
        isECS: boolean
      }
      maxRef: number
      vgpu: any
      npu: {
        npuIds: Array<any>
        npuName: string
      }
    }
    image: {
      id: number
      name: string
      alias: string
      ownerId: number
      public: boolean
      hostname: string
      creds: string
      ports: Array<{
        srcPort: number
        destPort: number
        forceBind: boolean
        url: string
        protocol: number
        proxy: any
        index: number
      }>
      description: string
      createTime: {
        seconds: number
      }
      lastModifyTime: {
        seconds: number
      }
      refCount: number
      attributes: {
        supportTmux: boolean
        isNVIDIA: boolean
        isCPU: boolean
        isAtlas: boolean
        isAMD64: boolean
        isAArch64: boolean
        isWin: boolean
      }
      cache_urls: any
      cache_url: string
      status: number
      storeStatus: number // 0:Invalid, 3:Pending, 4:Uploaded, 5:Formated、6:Finished
      storeService: string
      ownerType: number
      licenseType: number
      config: any
      weight: number
      format: number // 0:OverLayer2, 1:ZfsVolume, 2:BlockDevice
      domain: number
      sourceType: number
      instID: number
      bootMode: number
    }
    createTime: {
      seconds: number
    }
    url: string
    finishTime: any
    ports: Array<{
      srcPort: number
      destPort: number
      forceBind: boolean
      url: string
      protocol: number
      proxy: any
      index: number
    }>
    hwIDs: Array<number>
    version: string
    baseVmImage: any
    domain: number
    resource: {
      gpuName: string
      cpuName: string
    }
  }
  status: number
  userId: number
  billType: number
  amount: number
  state: {
    status: number
    log: Array<{
      id: number
      status: number
      ts: {
        seconds: number
      }
      sourceId: number
    }>
  }
  cmd: string
  envs: string
  mountInfos: Array<{
    host: string
    source: string
    dest: string
    key: string
    accessId: string
    readOnly: boolean
    type: number
  }>
  teamMountInfos: Array<{
    host: string
    source: string
    dest: string
    key: string
    accessId: string
    readOnly: boolean
    type: number
  }>
  couponAmountYuan: number
  coupons: Array<any>
  paymentAmount: number
  eventLog: {
    events: Array<{
      type: number
      timestamp: string
      pullEv: {
        progress: number
      }
    }>
    id: string
    sourceID: string
  }
  snapshots: Array<any>
  runningTimeSec: number
  order: ServerInstanceOrder
  totalBonus: {
    timeSec: number
    moneyCent: number
  }
  flag: any
  snapshotSizeBytes: number
  IsProprietary: boolean
  desc: {
    name: string
    iconURL: string
    mountInfos: any
  }
  migration: {
    supported: boolean
    availableTimes: number
  }
  sshAuths: Array<{
    BaseOn: {
      Cred: {
        id: number
        user: string
        password: string
      }
    }
  }>
  displayID: string
  nets: Array<NetGroup>
  agentID: number
  agentFlag: {
    id: number
    subject: string
    createTime: any
  }
  fromSnapshots: Array<{
    id: number
    path: string
    type: number
    status: number
    errno: number
    subject: string
    attributes: {
      isReleaseNode: boolean
      isIsolatedStore: boolean
    }
    ownerName: string
    createTime: {
      seconds: number
    }
    OwnerType: number
  }>
  nats: Array<{
    net: {
      netResID: string
      networkName: string
      edpResID: string
      cidr: string
      mac: string
      isAvail: boolean
    }
    portRange: {
      max: number
      min: number
    }
    portMappings: {
      items: Array<{
        targetPort: number
        listenPort: number
      }>
    }
  }>
}

export type ServerImageInfo = {
  id: number
  resID: string
  alias: string
  public: boolean
  ports: Array<{
    srcPort: number
    destPort: number
    forceBind: boolean
    url: string
    protocol: number
    proxy: any
    index: number
  }>
  description: string
  cached: boolean
  attributes: {
    supportTmux: boolean
    isNVIDIA: boolean
    isCPU: boolean
    isAtlas: boolean
    isAMD64: boolean
    isAArch64: boolean
    isWin: boolean
  }
  status: number
  storeStatus: number
  storeService: string
  format: number
  errCode?: number
  provider?: number
  ownerID: number
  ownerDeleted: boolean
  ownerName: string
  name: string
  repoUsername: string
  createdTime: number
  ownerType: number
  extend: any
  licenseType: number
  url: string
  hostname: string
  size: number
  minResource?: {
    cpus: number
    ramMB: number
    diskGB: number
  } | null
}

export type ServerScaleSpecListInfo = {
  resID: string
  class: string
  createTime: {
    seconds: number
  }
  enabled: boolean
}

export type ServerScaleMetricListInfo = {
  matrixArray: Array<{
    matrix: Array<number>
  }>
  name: string
}

export type ServerScaleSpecInfo = {
  id: number
  ownerID: number
  class: string
  minReplicas: number
  maxReplicas: number
  runtimeEnvs: Array<{
    type: number
    systemEnv: {
      fromImageId: number
      fromSnapshotId: number
      cmd: string
      envs: string
      extraPorts: number[]
    }
    hardwareEnv: {
      resource: {
        cpus: number
        gpus: number
        diskGB: number
        gpuName: string
        domain: string
        agentDomains: number[]
        agentDriver: number
        cpuName: string
        ramMB: number
        gpuRamMB: number
        attributeTags: number
        machineClassTag: string
        machineCategory: number
        unitQty: number
      }
    }
  }>
  metrics: Array<{
    type: number
    name: string
    desc: string
    target: {
      type: number
      value: number
    }
    url: string
  }>
  behavior: {
    scaleIn: {
      stabilizationWindowSeconds: number
      policies: Array<{
        type: number
        value: number
        periodSeconds: number
      }>
    }
    scaleOut: {
      stabilizationWindowSeconds: number
      policies: Array<{
        type: number
        value: number
        periodSeconds: number
      }>
    }
    toleranceRatio: number
  }
  createTime: {
    seconds: number
  }
  updateTime: {
    seconds: number
  }
  enabled: boolean
  deleted: boolean
}

export type ServerScaleSpecStat = {
  TotalCnt: number
  InitingCnt: number
  RunningCnt: number
  ReleasingCnt: number
  OthersCnt: number
}

export type ServerSnapInfo = {
  id: string
  resID: string
  nodeID: string
  path: string
  requestTime: string
  generatedTime: string
  // Absent when the snapshot has no label yet
  flag?: {
    id: string
    subject: string
    createTime: string
  }
  enabled: boolean
  status: number
  mismatchReason: number
  nodeInfo: {
    node: {
      image: {
        id: string
        resID: string
        alias: string
        ownerId: string
        public: boolean
        ports: Array<{
          srcPort: string
          protocol: number
        }>
        description: string
        createTime: string
        lastModifyTime: string
        refCount: number
        attributes: {
          supportTmux: boolean
        }
        ownerType: number
      }
      ports: Array<{
        srcPort: string
        destPort: string
        url: string
        protocol: number
      }>
      hwQty: string
    }
    envs: string
    mountInfos: Array<{
      source: string
      dest: string
    }>
  }
  sizeByte: string
  imageStatus: number
  minDiskGB: number
}

export type ServerClusterInfo = {
  resID: string
  name: string
  createTime: {
    seconds: number
  }
  clusterType: number
  domain: number
  capacity: {
    used: number
    total: number
  }
}

export type StorageBillInfo = {
  priceMillicent: number
  usedTimeSec: number
  sizeByte: number
}

export type ServerBillInfo = {
  billTime: {
    seconds: number
  }
  amount: number
  type: number
  sourceID: number
  Info: {
    ImageBillInfo?: StorageBillInfo
    SnapshotBillInfo?: StorageBillInfo
    BillingInfo?: {
      usedTimeSec: number
      hardware: {
        priceMillicent: number
        machine: {
          cpuIds: Array<number>
          cpuName: string
          ramMB: number
          diskGB: number
          diskName: string
          diskBandwidthMBs: number
          motherboardName: string
          pcieVer: string
          internetUploadMbs: number
          internetDownloadMbs: number
          totalCPUCores: number
          totalRAMMB: number
          bandwidthBit: number
          totalBandwidthBit: number
          arch: number
          pciConfig: any
          unitConfig: any
          gpuResourceLimit: any
        }
        gpu: {
          gpuIds: Array<number>
          gpuName: string
          gpuRamMB: number
          gpuMemBandwidthGBs: number
          gpuPCIELanes: number
          gpuPCIEBandwidthGBs: number
          maxCUDAVer: string
          tflops: number
          driver: string
          gpuModel: string
          uuids: any
          vendor: number
        }
        deadlineTime: {
          seconds: number
        }
        discountPriceMillicent: number
        timeLimitSec: number
        attributes: {
          isSelfOwner: boolean
          isThirdParty: boolean
          isSpecialPrice: boolean
          isBetaTest: boolean
          isXEnabled: boolean
          isSnapEnabled: boolean
          isComposable: boolean
          isVGPU: boolean
          isMIG: boolean
          isDistributed: boolean
          isHyperContainer: boolean
          isWinEnabled: boolean
          isSupportNodeOp: boolean
          isECS: boolean
        }
        maxRef: number
        vgpu: any
        npu: {
          npuIds: Array<any>
          npuName: string
        }
      }
      couponAmountYuan: number
      nodeStatus: number
      totalBonus: {
        timeSec: number
        moneyCent: number
      }
    }
    NetworkBillInfo?: {
      priceMillicent: number
      usedTimeSec: number
    }
    HwItemBillInfo?: any
    TimeBonusBillInfo?: any
    PayGroupBonusInfo?: any
    PurchasedBonusInfo?: any
    TransferInfo?: {
      fromUser?: number
      toUser?: number
      fromUserName?: string
      toUserName?: string
      reason?: number
    }
    GroupInfo?: any
    StorageBillInfo?: any
    BillOperationInfo?: any
  }
  userID: number
  deadlineTime: any
  sourceType: number
  displayID: string
  timeInfo: {
    startTime: {
      seconds: number
    }
    endTime?: {
      seconds: number
    }
  }
  subType: number
}

export type ServerItemInfo = {
  id?: number
  resID: string
  catalog: number
  paymentType: number
  prices: Array<{
    type: number
    priceCent: number
    qty?: number
  }>
  desc: any
  domain: number
}

export type ServerMeshEndpointInfo = {
  resID: string
  status: number
  cidr: string
  mac: string
  createTime: {
    seconds: number
  }
  ownerID?: number
  ownerType?: number
  instResID?: string
  flag?: {
    id: number
    subject?: string
    createTime?: {
      seconds: number
    }
  }
  endpointInfo?: {
    node?: ServerMeshEndpointNodeInfo
  }
  isPurchased?: boolean
}

export type ServerMeshEndpointNodeInfo = {
  resID: string
  gpuName?: string
  cpuName?: string
  imageAlias?: string
  nodeStatus?: number
  bandwidthBit?: number
  displayID?: string
  gpuCores?: number
  cpuCores?: number
  flag?: {
    id: number
    subject?: string
    createTime?: {
      seconds: number
    }
  }
}

export type ServerAttachmentInfo = {
  requested: {
    status: number
    cpuAttachments: {
      resIDs: string[] | null
      priceMillicentPerUnit: number
      coresPerUnit: number
    }
    ramAttachments: {
      resIDs: string[] | null
      priceMillicentPerUnit: number
      MBPerUnit: number
    }
  }
  available: {
    status: number
    cpuAttachments: {
      resIDs: string[] | null
      priceMillicentPerUnit: number
      coresPerUnit: number
    }
    ramAttachments: {
      resIDs: string[] | null
      priceMillicentPerUnit: number
      MBPerUnit: number
    }
  }
}

export type ServerDeploymentInfo = {
  resID: string
  name: string
  description: string
  gateway: {
    resID: string
    methods: string[] | null
    url: string
    port: number
    public: boolean
  }
  runtimeEnv: {
    type: number
    systemEnv: {
      fromImageId: number
      fromSnapshotId: number
      cmd: string
      envs: string
      extraPorts: number[]
    }
    hardwareEnv: {
      resource: {
        cpus: number
        gpus: number
        diskGB: number
        gpuName: string
        domain: string
        agentDomains: number[]
        agentDriver: number
        cpuName: string
        ramMB: number
        gpuRamMB: number
        attributeTags: number
        machineClassTag: string
        machineCategory: number
        unitQty: number
      }
    }
  }
  totalCostCent: number
  ttlSeconds: number
  maxReplicas: number
  scalingMode?: 0 | 1
  concurrencyPerReplica?: number
  maxPendingRequests?: number
  credentials?: ServerCredentialInfo[]
}

export type ServerDeploymentBillInfo = {
  instResID: string
  instStatus: number
  createTime: {
    seconds: number
    nanos: number
  }
  finishTime?: {
    seconds?: number
    nanos?: number
  }
  priceCent: number
  costCent: number
  runningDurationSec: number
}

export type ServerGatewayInfo = {
  resID: string
  host: string
  public: boolean
  domain: number
}

export type ServerSshPubKeyInfo = {
  id: string
  key: string
}

export type ServerNATInfo = {
  resID: string
  name: string
  createTime: {
    seconds: number
  }
  clusterType: number
  domain: number
  capacity: { used: number; total: number } | null
  amount: number
  bandwidth: {
    rateBitsPerSec: number
    ceilBitsPerSec: number
    burstBytes: number
    cburstBytes: number
    bandwidthType: number
  } | null
}

// Private Region Types
export type ServerPrivateRegionInfo = {
  region: number
  resID: string
  name: string
  desc: string
  owner: string
  createTime: {
    seconds: number
    nanos?: number
  }
}

export type ServerRegionInfo = {
  region: number
  ownerType: number // 0: Official, 1: User
  ownerID: number
  name: string
  desc: string
  createTime: {
    seconds: number
    nanos?: number
  }
}

export type ServerPrivateRegionNodeDetails = {
  resID: string
  name: string
  status: number
  cpuModel: string
  gpuModel: string
  diskGB: number
  cpuCores: number
  ramMB: number
  gpuNum: number
  remark: string
  regionInfo: ServerRegionInfo
}

export type ServerPrivateRegionNodeHardware = {
  category: number
  cpuName: string
  diskGiB: number
  gpuName: string
  gpuNum: number
  priceUSDMillicent: number
  ramMiB: number
  status: number
  vCpuCores: number
}

export type ServerPrivateRegionHardwareQuantityStats = {
  total: number
  online: number
  occupied: number
}

export type ServerPrivateRegionHardwareModelStats = {
  model: string
  category: number
  total: number
  online: number
  leased: number
  vGPUs: number
}

export type ServerPrivateRegionNodeHardwareBrief = {
  quantityStats: ServerPrivateRegionHardwareQuantityStats
  modelStats: ServerPrivateRegionHardwareModelStats[]
}

export type ServerPrivateRegionLeasedTimeMetric = {
  timestamp: number
  leasedHour: number
  totalHour: number
}

// Team Types
export type ServerTeamInfo = {
  id: number
  ownerID: number
  name: string
  introduction: string
  createTime: {
    seconds: number
  }
  profile: {
    limit: {
      membersNumLimit: number
      adminsNumLimit: number
    }
  }
  desc: {
    resID: string
    domainName: string
  }
  status: number
  deleted: boolean
}

export type ServerTeamUser = {
  name: string
  role: number
  nickname: string
  note: string
}

// Elastic IP Types
export type ServerEIPInfo = {
  resID: string
  wanIP: string
  status: number // 1 = available
  priceMillicent: number
  region: number
}

// ACL Types
// type: 0=Invalid, 1=PublicIP
// status: 0=InvalidStatus, 1=Active, 2=Inactive
export type ServerACLResource = {
  resID: string
  name: string
  type: ACLResourceType
  status: ACLResourceStatus
  updatedAt: { seconds: number }
}

// direction: 0=Ingress, 1=Egress
// defaultAction: 0=Allow, 1=Deny
export type ServerACLL3Rule = {
  id: number
  netResID: string
  aclResID: string
  remoteCIDR: string
  direction: ACLDirection
  defaultAction: ACLAction
  enabled: boolean
  description: string
  version: number
  createdAt: { seconds: number }
  updatedAt: { seconds: number }
  state: {
    status: ACLL3RuleState
    message: string
    updatedAt?: { seconds: number }
  }
}

// protocol: 'ANY' | 'ICMP' | 'TCP' | 'UDP' (response uses string form)
export type ServerACLL4Rule = {
  id: number
  l3RuleID: number
  protocol: string
  portStart: number
  portEnd: number
  action: ACLAction
  enabled: boolean
  description: string
  version: number
  createdAt: { seconds: number }
  updatedAt: { seconds: number }
}

export type ServerTeamVolume = {
  allocPart: {
    usage_mb: number
    quota_gb: number
    init_quota_gb: number
    id: number
    type: number
    domain: number
    res_id: string
    last_quota_gb: number
    usage_bytes: number
  }
  subParts: Array<{
    usage_mb: number
    quota_gb: number
    init_quota_gb: number
    id: number
    type: number
    domain: number
    res_id: string
    last_quota_gb: number
    usage_bytes: number
  }>
  services?: Record<
    string,
    {
      services: {
        '9'?: {
          accessableServices: Array<{
            label: string
            value: string
            weight: number
            authToken: string
          }>
        }
        '12'?: {
          accessableServices: Array<{
            label: string
            value: string
            weight: number
            authToken: string
          }>
        }
      }
    }
  >
  expireTime?: { seconds: number }
}

// Instance Spec Types (Private Region)
export type ServerPrivateRegionAgentInstance = {
  resID: string
  owner: string
  label: string
  status: number
}

export type ServerPrivateRegionVGpuSpec = {
  divisionCount: number
  resID: string
  title: string
  vGpuRamMB: number
  vGpuCores: number
}

export type ServerPrivateRegionNodeSpec = {
  resID: string
  category: number // 0: GPU, 1: CPU, 2: VGPU
  units: number
  cpuCores: number
  ramMB: number
  diskGB: number
  gpuNum: number
  gpuRamMB: number
  vgpuSpecId: string
  vGpuSpec: ServerPrivateRegionVGpuSpec | null // API returns object for vGPU, null for others
  enabled: boolean
  // Extended fields for Instance Spec card UI
  vgpuGpc?: number // vGPU GPC count
  totalCpuCores?: number // Total CPU cores (for allocation ratio display)
  totalRamMB?: number // Total RAM in MB (for allocation ratio display)
  totalDiskGB?: number // Total disk in GB (for allocation ratio display)
}

// AK/SK Credential
export enum CredentialResourceType {
  Deployment = 1,
  DeploymentClass = 2,
}

export type ServerCredentialResource = {
  resIDs: string[]
  resourceType: CredentialResourceType
}

export type ServerCredentialInfo = {
  resID: string
  accessKey: string
  title: string
  description: string
  validDurationSec: number
  enabled: boolean
  createdAt: { seconds: number }
  expiresAt: { seconds: number }
  rotatedAt: { seconds: number }
  resources: ServerCredentialResource[]
}
